import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";

import "./QuoteApproval.scss";

import { ReactComponent as CargobaseLogoIcon } from "assets/images/icons/CargobaseLogo.svg";
import Card from "components/v2/Card/Card";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import EmptyState from "components/v2/EmptyState/EmptyState";
import { quickApproveQuotation } from "api";
import { ReactComponent as FeaturedIcon } from "assets/images/iconsV2/featured-icon-gray.svg";
import Button from "components/v2/Button/Button";
import QuoteApprovalCard, { QuoteApprovalCardProps } from "./components/QuoteApprovalCard";
import { APPROVAL_TYPES, quickApprovalResponseDefaultOptions, quickApprovalResponseOptions } from "./QuoteApprovalHelper";

function QuoteApproval() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [approvalResponse, setApprovalResponse] = useState<QuoteApprovalCardProps | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInvalidToken, setIsInvalidToken] = useState(false);
  const [isError, setIsError] = useState(false);
  const token = searchParams.get("token");
  const action = searchParams.get("action");

  useEffect(() => {
    if (!token || !action) {
      setIsError(true);
      setIsLoading(false);
      return;
    }
    const payload = {
      token,
      action,
    };
    quickApproveQuotation({}, { params: payload })
      .then((res) => {
        if (res.data?.detail === APPROVAL_TYPES.REDIRECT_TO_QUOTE && res.data?.shipment_id) {
          navigate(`/shipment/${res.data?.shipment_id}`);
          // eslint-disable-next-line no-prototype-builtins
        } else if (res.data?.hasOwnProperty("further_approval_needed")) {
          if (res.data.further_approval_needed) {
            setApprovalResponse({
              ...quickApprovalResponseDefaultOptions(res.data),
              showIcon: true,
              title: "Quote approved",
              description:
                "Your approval of the quote has been recorded. An another request has been sent out to all relevant next-level approvers.",
            });
          } else {
            setApprovalResponse({
              ...quickApprovalResponseDefaultOptions(res.data),
              showIcon: true,
              title: "Quote approved and booked",
              description: "You have sucessfully approved the quote.",
            });
          }
        }
      })
      .catch((err) => {
        const { detail, ...rest } = err?.response?.data || {};
        if (detail && quickApprovalResponseOptions[detail]) {
          setApprovalResponse(quickApprovalResponseOptions[detail](rest));
        } else if (detail && detail === APPROVAL_TYPES.TOKEN_EXPIRED) {
          setIsInvalidToken(true);
        } else {
          setIsError(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, action]);

  return (
    <div className="quote-approval">
      <CargobaseLogoIcon />
      <div className="mt-7xl">
        <div className="d-flex justify-content-center">
          {isLoading && (
            <Card borderRadius="sm" shadow="sm" style={{ width: "640px" }}>
              <LoadingSkeleton rows={3} />
            </Card>
          )}
          {!isLoading && (isInvalidToken || isError) && (
            <Card borderRadius="sm" shadow="sm" style={{ width: "640px" }}>
              <Card.Body>
                <EmptyState
                  noActions={isError}
                  customIcon={<FeaturedIcon />}
                  title={
                    isInvalidToken
                      ? intl.formatMessage({
                          id: "general.sessionTokenExpired",
                          defaultMessage: "Your session token has expired.",
                        })
                      : intl.formatMessage({
                          id: "general.couldNotLoadDetails",
                          defaultMessage: "We couldn't load these details",
                        })
                  }
                  description={
                    isInvalidToken
                      ? intl.formatMessage({
                          id: "general.pleaseLoginAgain",
                          defaultMessage: "Please log in again",
                        })
                      : intl.formatMessage({
                          id: "general.tryAgainOrCheckBackLater",
                          defaultMessage: "You can try again or check back later.",
                        })
                  }
                  {...(isInvalidToken && {
                    customActions: <Button label="Log in" variant="primary" onClick={() => navigate("/")} />,
                  })}
                />
              </Card.Body>
            </Card>
          )}
          {!isLoading && !isError && !isInvalidToken && approvalResponse && <QuoteApprovalCard {...approvalResponse} />}
        </div>
      </div>
    </div>
  );
}

export default QuoteApproval;
